import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, AlertTriangle } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "polling", attempts: 0 });
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus({ payment_status: "missing" }); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (cancelled) return;
        setData(data);
        if (data.payment_status === "paid") setStatus({ payment_status: "paid" });
        else if (data.status === "expired") setStatus({ payment_status: "expired" });
        else if (attempts >= 8) setStatus({ payment_status: "timeout" });
        else setTimeout(poll, 2000);
      } catch {
        if (attempts >= 5) setStatus({ payment_status: "error" });
        else setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <AppLayout>
      <div className="max-w-xl mx-auto pp-card-elevated p-10 text-center" data-testid="payment-success-card">
        {status.payment_status === "paid" ? (
          <>
            <CheckCircle2 size={56} className="mx-auto text-[#00E676]" />
            <h1 className="font-heading text-3xl font-black mt-4">Payment received</h1>
            {data && (
              <div className="mt-3 text-sm space-y-1">
                <p className="text-zinc-400">
                  <span className="text-white font-medium">{data.amount} {data.currency?.toUpperCase()}</span> paid
                  {" · "}<span className="text-zinc-500">{data.platform_fee} platform fee</span>
                </p>
                {data.split_payment && (
                  <p className="text-xs text-[#00E676]" data-testid="split-payment-banner">
                    {data.payee_amount} {data.currency?.toUpperCase()} routed directly to the organizer's account
                  </p>
                )}
              </div>
            )}
          </>
        ) : status.payment_status === "expired" || status.payment_status === "timeout" || status.payment_status === "error" ? (
          <>
            <AlertTriangle size={56} className="mx-auto text-[#FF3B30]" />
            <h1 className="font-heading text-3xl font-black mt-4">{status.payment_status === "expired" ? "Session expired" : "Payment not confirmed"}</h1>
            <p className="text-zinc-400 mt-2">Please retry from your invoices page.</p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 mx-auto rounded-full border-2 border-[#00E676] border-t-transparent animate-spin" />
            <h1 className="font-heading text-3xl font-black mt-4">Confirming payment...</h1>
            <p className="text-zinc-400 mt-2">Hold tight, this only takes a few seconds.</p>
          </>
        )}
        <Link to="/invoices" data-testid="back-to-invoices" className="pp-btn-primary px-6 py-3 rounded-xl mt-8 inline-flex items-center gap-2">Back to invoices <ArrowRight size={16} /></Link>
      </div>
    </AppLayout>
  );
}
